import { useEffect } from "react";
import { useTrustNGOSession } from "@/hooks/useTrustNGOSession";
import { getAuthBaseUrl, hasSigmacertAdminAccess, loginWithTrustNGO, logout, validateTenantAccess } from "@/lib/trustngo-auth";
import { Button } from "@/components/ui/button";
import { ShieldX } from "lucide-react";

const SsoRedirect = () => {
  useEffect(() => {
    loginWithTrustNGO();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <p className="text-muted-foreground">Redirecting to TrustNGO login...</p>
    </div>
  );
};

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { session, loading } = useTrustNGOSession();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  // Check authentication first
  if (!session) {
    return <SsoRedirect />;
  }

  // Check tenant access
  const tenantValidation = validateTenantAccess(session);
  if (!tenantValidation.valid) {
    if (tenantValidation.error === "ACCESS_DENIED") {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-8 text-center">
          <ShieldX className="h-16 w-16 text-destructive" />
          <h1 className="text-2xl font-bold">Access Denied</h1>
          <p className="text-muted-foreground max-w-md">
            Your account does not have access to the SigmaCert platform. Please contact your organization administrator or request access.
          </p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => logout()}>Back to TrustNGO</Button>
            <Button onClick={() => window.open(getAuthBaseUrl(), "_blank")}>
              Request Access
            </Button>
          </div>
        </div>
      );
    }
  }

  if (!hasSigmacertAdminAccess(session)) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-8 text-center">
        <ShieldX className="h-16 w-16 text-destructive" />
        <h1 className="text-2xl font-bold">Admin Access Required</h1>
        <p className="text-muted-foreground max-w-md">
          Your account can access the SigmaCert workspace, but it does not have admin permissions.
        </p>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => (window.location.href = "/workspace")}>
            Go to Workspace
          </Button>
          <Button onClick={() => logout()}>Switch Account</Button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
